import ApiService from './api';

/**
 * Service for DNI lookup functionality
 * Handles automatic person data retrieval from MIGO system
 */
class DniLookupService {
  /**
   * Search person data by DNI
   * @param {string} dni - DNI number (8 digits)
   * @returns {Promise<Object>} Person data response
   */
  async searchByDni(dni) {
    try {
      // Validate DNI format
      if (!dni || typeof dni !== 'string') {
        throw new Error('DNI inválido');
      }

      const cleanDni = dni.trim();
      
      // Validate DNI has exactly 8 digits
      if (cleanDni.length !== 8 || !/^\d+$/.test(cleanDni)) {
        throw new Error('El DNI debe tener exactamente 8 dígitos');
      }

      console.log('Searching DNI:', cleanDni);

      const response = await ApiService.post('/api/V1/migo/dni', {
        dni: cleanDni
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: 'Datos encontrados exitosamente'
        };
      } else {
        // Handle API error response
        return {
          success: false,
          message: response.message || 'No se encontraron datos para este DNI',
          error: response.error
        };
      }
    } catch (error) {
      console.error('DNI Lookup Error:', error);
      return {
        success: false,
        message: error.message || 'Error al buscar datos del DNI',
        error: error
      };
    }
  }

  /**
   * Format person data for form autocomplete
   * @param {Object} personData - Raw person data from API
   * @returns {Object} Formatted data for form fields
   */
  formatPersonDataForForm(personData) {
    if (!personData) return {};

    // Parse the nombre field which contains the full name
    // Format: "PRIMER_APELLIDO SEGUNDO_APELLIDO NOMBRES..."
    // Las primeras 2 palabras son SIEMPRE apellidos, el resto son nombres
    const fullName = personData.nombre || '';
    const nameParts = fullName.trim().split(' ').filter(part => part.length > 0);

    let firstName = '';
    let lastName = '';

    if (nameParts.length >= 3) {
      // Primeras 2 palabras: apellidos, resto: nombres
      const primerApellido = nameParts[0];
      const segundoApellido = nameParts[1];
      const nombres = nameParts.slice(2).join(' ');

      lastName = `${primerApellido} ${segundoApellido}`;
      firstName = nombres;
    } else if (nameParts.length === 2) {
      // Solo 2 palabras: ambas son apellidos
      lastName = `${nameParts[0]} ${nameParts[1]}`;
      firstName = '';
    } else if (nameParts.length === 1) {
      // Solo 1 palabra: es apellido
      lastName = nameParts[0];
      firstName = '';
    }

    return {
      firstName: firstName,
      lastName: lastName,
      identification_type: '1', // Always DNI for this API
      identification_number: personData.dni || '',
      // Keep existing form data for fields not provided by the API
      phone: '', // This should be filled by user
      birthDate: '' // This should be filled by user
    };
  }

  /**
   * Check if DNI is complete and valid for lookup
   * Only works for DNI (8 digits), not for other document types
   * @param {string} dni - DNI input value
   * @returns {boolean} Whether DNI is ready for lookup
   */
  isDniReadyForLookup(dni) {
    if (!dni || typeof dni !== 'string') return false;
    const cleanDni = dni.trim();
    // Only validate DNI format: exactly 8 numeric digits
    return cleanDni.length === 8 && /^\d+$/.test(cleanDni);
  }
}

export default new DniLookupService();
